import React, { useState, useEffect } from 'react';
import {
  Cloud, CloudRain, RefreshCw, ArrowUpCircle, ArrowDownCircle,
  CheckCircle2, AlertTriangle, Wifi, WifiOff, Server, ShieldCheck,
  Loader2, Settings as SettingsIcon, Clock, Layers, ShoppingBag,
  CreditCard, DollarSign, Activity, Eye, EyeOff, Save, Sparkles,
  ExternalLink, FileText, Check, HelpCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getCloudSyncStatus,
  saveCloudSyncConfig,
  testCloudConnection,
  pushSalesToCloud,
  pullCatalogFromCloud,
  pushCatalogToCloud,
  pullWebOrdersFromCloud,
  getCloudSyncLogs,
  type CloudSyncStatusResponse,
  type CloudSyncLog,
} from '../../services/CloudSyncService';

interface CloudSyncManagerProps {
  embedded?: boolean;
}

export function CloudSyncManager({ embedded = false }: CloudSyncManagerProps) {
  const [data, setData] = useState<CloudSyncStatusResponse | null>(null);
  const [logs, setLogs] = useState<CloudSyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingConn, setTestingConn] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [syncingSales, setSyncingSales] = useState(false);
  const [pullingCatalog, setPullingCatalog] = useState(false);
  const [pushingCatalog, setPushingCatalog] = useState(false);
  const [pullingWebOrders, setPullingWebOrders] = useState(false);

  // Config Form
  const [cloudUrl, setCloudUrl] = useState('');
  const [cloudToken, setCloudToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    latency_ms?: number;
  } | null>(null);

  const loadStatus = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getCloudSyncStatus();
      setData(res);
      setCloudUrl(res.config.cloud_url || '');
      setAutoSync(res.config.auto_sync);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al obtener el estado de sincronización');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const logsRes = await getCloudSyncLogs();
      setLogs(logsRes);
    } catch {
      // Silencioso
    }
  };

  useEffect(() => {
    loadStatus();
    loadLogs();
  }, []);

  const handleTestConnection = async () => {
    setTestingConn(true);
    setTestResult(null);
    try {
      const res = await testCloudConnection({
        cloud_url: cloudUrl,
        cloud_token: cloudToken || undefined,
      });
      setTestResult(res);
      if (res.success) {
        toast.success(`Conexión exitosa con la nube (${res.latency_ms} ms)`);
      } else {
        toast.warn(res.message);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Error al conectar con el servidor de nube';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTestingConn(false);
    }
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingConfig(true);
    try {
      const res = await saveCloudSyncConfig({
        cloud_url: cloudUrl,
        cloud_token: cloudToken || undefined,
        auto_sync: autoSync,
      });
      toast.success(res.message || 'Configuración de sincronización guardada');
      setCloudToken(''); // Limpiar token visible por seguridad
      await loadStatus(true);
      await loadLogs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al guardar configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const handlePushSales = async () => {
    setSyncingSales(true);
    try {
      const res = await pushSalesToCloud();
      if (res.success) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
      await loadStatus(true);
      await loadLogs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al sincronizar ventas a la nube');
    } finally {
      setSyncingSales(false);
    }
  };

  const handlePullCatalog = async () => {
    setPullingCatalog(true);
    try {
      const res = await pullCatalogFromCloud();
      if (res.success) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
      await loadStatus(true);
      await loadLogs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al descargar catálogo');
    } finally {
      setPullingCatalog(false);
    }
  };

  const handlePushCatalog = async () => {
    if (!confirm('¿Deseas enviar todo el catálogo maestro local hacia la nube?')) return;
    setPushingCatalog(true);
    try {
      const res = await pushCatalogToCloud();
      if (res.success) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
      await loadStatus(true);
      await loadLogs();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al subir catálogo a la nube');
    } finally {
      setPushingCatalog(false);
    }
  };

  const handlePullWebOrders = async () => {
    setPullingWebOrders(true);
    try {
      const res = await pullWebOrdersFromCloud();
      if (res.success) {
        if (res.imported > 0) {
          toast.success(`✓ Se importaron ${res.imported} pedidos web al restaurante.`);
        } else {
          toast.info('No hay pedidos web nuevos pendientes en la nube.');
        }
        await loadStatus(true);
        await loadLogs();
      } else {
        toast.warn(res.message || 'No se pudieron consultar pedidos web.');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al descargar pedidos web');
    } finally {
      setPullingWebOrders(false);
    }
  };

  const conn = data?.connection;
  const pending = data?.pending;
  const config = data?.config;

  return (
    <div className={`space-y-6 ${embedded ? '' : 'max-w-6xl mx-auto p-4 md:p-8'}`}>
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-gray-200 dark:border-neutral-700">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-tr from-sky-500 via-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20">
              <Cloud size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Sincronización Nube ⇄ Local
                </h2>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                  <ShieldCheck size={12} />
                  Solo Admin
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Monitorea, transfiere y sincroniza pedidos, pagos y productos entre este servidor local y la nube
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowConfig(!showConfig)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition ${
              showConfig
                ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-300'
                : 'border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800'
            }`}
          >
            <SettingsIcon size={16} />
            <span>Configurar Nube</span>
          </button>
          <button
            type="button"
            onClick={() => setShowLogs(!showLogs)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition ${
              showLogs
                ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-300'
                : 'border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800'
            }`}
          >
            <FileText size={16} />
            <span className="hidden sm:inline">Historial</span>
          </button>
          <button
            type="button"
            onClick={() => { loadStatus(); loadLogs(); }}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition disabled:opacity-50"
            title="Refrescar métricas"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </div>

      {/* Banner de Estado de Conexión en Vivo */}
      <div className={`p-5 rounded-2xl border transition-all ${
        conn?.status === 'connected'
          ? 'bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50/50 dark:from-emerald-950/30 dark:via-teal-950/20 dark:to-neutral-800 border-emerald-200 dark:border-emerald-800/60'
          : conn?.status === 'unconfigured'
          ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50/50 dark:from-amber-950/30 dark:via-orange-950/20 dark:to-neutral-800 border-amber-200 dark:border-amber-800/60'
          : 'bg-gradient-to-r from-red-50 via-rose-50 to-red-50/50 dark:from-red-950/30 dark:via-rose-950/20 dark:to-neutral-800 border-red-200 dark:border-red-800/60'
      }`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className={`p-3 rounded-xl shrink-0 ${
              conn?.status === 'connected'
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                : conn?.status === 'unconfigured'
                ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                : 'bg-red-500 text-white shadow-md shadow-red-500/20'
            }`}>
              {conn?.status === 'connected' ? (
                <Wifi size={22} />
              ) : conn?.status === 'unconfigured' ? (
                <AlertTriangle size={22} />
              ) : (
                <WifiOff size={22} />
              )}
            </div>

            <div>
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${
                  conn?.status === 'connected'
                    ? 'bg-emerald-500 animate-pulse'
                    : conn?.status === 'unconfigured'
                    ? 'bg-amber-500'
                    : 'bg-red-500'
                }`} />
                <h3 className="font-bold text-base text-gray-900 dark:text-white">
                  {conn?.status === 'connected'
                    ? 'Conexión Activa con el Servidor Central'
                    : conn?.status === 'unconfigured'
                    ? 'Configuración Pendiente: Servidor en la Nube'
                    : 'Sin Conexión con el Servidor Central'}
                </h3>
                {conn?.latency_ms !== null && conn?.latency_ms !== undefined && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-neutral-700 shadow-2xs">
                    {conn.latency_ms} ms
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {conn?.message || 'Verificando conectividad con el servidor de la nube...'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {config?.cloud_url ? (
              <span className="font-mono bg-white/80 dark:bg-neutral-800 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 truncate max-w-xs" title={config.cloud_url}>
                {config.cloud_url}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setShowConfig(true)}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold transition shadow-xs"
              >
                Configurar URL y Token
              </button>
            )}

            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 bg-white/70 dark:bg-neutral-800/80 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-neutral-700">
              <Clock size={13} />
              <span>
                Última subida:{' '}
                <strong>
                  {config?.last_to_cloud_at
                    ? new Date(config.last_to_cloud_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
                    : 'Aún no'}
                </strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Formulario de Configuración (Desplegable) */}
      {showConfig && (
        <form onSubmit={handleSaveConfig} className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-gray-100 dark:border-neutral-700 shadow-sm space-y-4 animate-in fade-in duration-200">
          <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-neutral-700">
            <div className="flex items-center gap-2 text-gray-900 dark:text-white font-bold text-base">
              <SettingsIcon size={18} className="text-blue-600 dark:text-blue-400" />
              <span>Configuración de Servidor Central (Hostinger / VPS)</span>
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Guarda tus credenciales de nube
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cloud_url" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                URL de la API en la Nube
              </label>
              <input
                id="cloud_url"
                type="url"
                value={cloudUrl}
                onChange={e => setCloudUrl(e.target.value)}
                placeholder="https://tudominio.com/api"
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
              <p className="text-[11px] text-gray-400 mt-1">
                La dirección donde está alojado tu backend central en Hostinger o VPS.
              </p>
            </div>

            <div>
              <label htmlFor="cloud_token" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Token Secreto de Sincronización
                {config?.cloud_token_set && (
                  <span className="ml-2 text-emerald-600 dark:text-emerald-400 text-[11px] font-normal">
                    (Ya hay un token guardado)
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  id="cloud_token"
                  type={showToken ? 'text' : 'password'}
                  value={cloudToken}
                  onChange={e => setCloudToken(e.target.value)}
                  placeholder={config?.cloud_token_set ? '••••••••••••••••••••' : 'Ingresa el CLOUD_API_TOKEN'}
                  className="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Debe coincidir con la clave <code className="font-mono">CLOUD_API_TOKEN</code> configurada en el servidor de la nube.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={autoSync}
                onChange={e => setAutoSync(e.target.checked)}
                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span>Sincronizar automáticamente en segundo plano (cada 1 minuto)</span>
            </label>
          </div>

          {testResult && (
            <div className={`p-3 rounded-xl text-xs font-medium border flex items-center gap-2 ${
              testResult.success
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800'
            }`}>
              {testResult.success ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              <span>{testResult.message}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-neutral-700">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testingConn || !cloudUrl}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-700 transition disabled:opacity-50"
            >
              {testingConn ? <Loader2 size={16} className="animate-spin" /> : <Wifi size={16} />}
              <span>Probar Conexión</span>
            </button>

            <button
              type="submit"
              disabled={savingConfig}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/20 transition disabled:opacity-50"
            >
              {savingConfig ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              <span>Guardar Configuración</span>
            </button>
          </div>
        </form>
      )}

      {/* Tarjetas de Registros Pendientes de Sincronización */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Layers size={18} className="text-blue-600 dark:text-blue-400" />
            <span>Cola de Registros Locales Pendientes por Enviar</span>
          </h3>
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300">
            Total pendientes: {pending?.total || 0}
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Órdenes */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Órdenes / Pedidos</span>
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
                <ShoppingBag size={18} />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                {pending ? pending.orders : '—'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {data?.synced ? `${data.synced.orders} ya sincronizadas` : 'Sin procesar'}
              </p>
            </div>
          </div>

          {/* Pagos */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Pagos Registrados</span>
              <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
                <CreditCard size={18} />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                {pending ? pending.payments : '—'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {data?.synced ? `${data.synced.payments} ya sincronizados` : 'Sin procesar'}
              </p>
            </div>
          </div>

          {/* Sesiones de Caja */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Sesiones de Caja</span>
              <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400">
                <DollarSign size={18} />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                {pending ? pending.sessions : '—'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Aperturas y cierres de turno
              </p>
            </div>
          </div>

          {/* Movimientos de Caja */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Movimientos de Caja</span>
              <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
                <Activity size={18} />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                {pending ? pending.movements : '—'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Ingresos y egresos manuales
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Centro de Acciones de Sincronización */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Acción 1: Subir Ventas (Local -> Nube) */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between space-y-4 hover:border-blue-300 dark:hover:border-blue-700 transition">
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <ArrowUpCircle size={26} />
            </div>
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">
              Subir Ventas a la Nube
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Empuja todas las órdenes, pagos y turnos acumulados en este servidor local hacia la base de datos de la nube.
            </p>
          </div>

          <button
            type="button"
            onClick={handlePushSales}
            disabled={syncingSales || (pending?.total === 0)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm shadow-md shadow-blue-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncingSales ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Sincronizando Ventas...</span>
              </>
            ) : (
              <>
                <ArrowUpCircle size={16} />
                <span>Sincronizar Ventas Ahora</span>
              </>
            )}
          </button>
        </div>

        {/* Acción 2: Descargar Catálogo (Nube -> Local) */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between space-y-4 hover:border-emerald-300 dark:hover:border-emerald-700 transition">
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <ArrowDownCircle size={26} />
            </div>
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">
              Descargar Catálogo de Nube
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Actualiza productos, precios, categorías y sabores de este local con los datos más recientes configurados en el panel en la nube.
            </p>
          </div>

          <button
            type="button"
            onClick={handlePullCatalog}
            disabled={pullingCatalog}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-md shadow-emerald-500/20 transition disabled:opacity-50"
          >
            {pullingCatalog ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Descargando Menú...</span>
              </>
            ) : (
              <>
                <ArrowDownCircle size={16} />
                <span>Actualizar Menú Local</span>
              </>
            )}
          </button>
        </div>

        {/* Acción 3: Subir Catálogo (Local -> Nube) */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between space-y-4 hover:border-purple-300 dark:hover:border-purple-700 transition">
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center">
              <CloudRain size={26} />
            </div>
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">
              Subir Catálogo a la Nube
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Publica las categorías, productos, sabores y adiciones editados en este computador para que queden centralizados en la nube.
            </p>
          </div>

          <button
            type="button"
            onClick={handlePushCatalog}
            disabled={pushingCatalog}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm shadow-md shadow-purple-500/20 transition disabled:opacity-50"
          >
            {pushingCatalog ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Publicando Catálogo...</span>
              </>
            ) : (
              <>
                <Cloud size={16} />
                <span>Subir Catálogo a Nube</span>
              </>
            )}
          </button>
        </div>

        {/* Acción 4: Bajar Pedidos Web (Nube -> Local) */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between space-y-4 hover:border-amber-300 dark:hover:border-amber-700 transition">
          <div className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <ShoppingBag size={26} />
            </div>
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">
              Bajar Pedidos Web (PWA)
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Consulta e importa de inmediato los pedidos de autoservicio realizados por comensales desde la PWA en la nube.
            </p>
          </div>

          <button
            type="button"
            onClick={handlePullWebOrders}
            disabled={pullingWebOrders}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm shadow-md shadow-amber-500/20 transition disabled:opacity-50"
          >
            {pullingWebOrders ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Consultando Nube...</span>
              </>
            ) : (
              <>
                <ArrowDownCircle size={16} />
                <span>Bajar Pedidos Web</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Historial / Bitácora de Sincronizaciones */}
      {showLogs && (
        <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm overflow-hidden animate-in fade-in duration-200">
          <div className="p-5 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <FileText size={18} className="text-blue-600 dark:text-blue-400" />
              <h3 className="font-bold text-gray-900 dark:text-white text-base">
                Bitácora de Sincronizaciones Recientes
              </h3>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Registros guardados en auditoría
            </span>
          </div>

          {logs.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Aún no hay registros de sincronización recientes.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50/75 dark:bg-neutral-900/50 text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider border-b border-gray-100 dark:border-neutral-700">
                  <tr>
                    <th className="px-5 py-3">Fecha y Hora</th>
                    <th className="px-5 py-3">Operación</th>
                    <th className="px-5 py-3">Detalle</th>
                    <th className="px-5 py-3">Usuario</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-700/60">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50/50 dark:hover:bg-neutral-700/30">
                      <td className="px-5 py-3.5 whitespace-nowrap text-gray-600 dark:text-gray-400">
                        {new Date(log.created_at).toLocaleString('es-CO', {
                          dateStyle: 'short',
                          timeStyle: 'medium',
                        })}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap font-mono font-semibold text-gray-900 dark:text-white">
                        {log.action}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 dark:text-gray-300">
                        {log.description}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {log.user_name || 'Sistema'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
